import { Dialog } from '@mui/material';
import Slider from '@mui/material/Slider';
import { useEffect, useRef, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import getCroppedImg, { dataURLtoFile } from '../../common/cropImage';
import { detectHEIC } from '../../common/fileReaderSupport';
import { base64ToBlob } from '../../common/imageTypeConverter';
import { resizeFile } from '../../common/resizeImage';
import { MAX_CANVAS_SIZE } from '../../constant/numericParams';
import useToast from '../../hooks/useToast';
import GreyLoadingOverlay from '../loading/GreyLoadingOverlay';

const AvatarCropper = ({
  open,
  toggle,
  onError = () => {},
  onSuccess = () => {},
  asyncFunc,
}: {
  open: boolean;
  toggle: () => void;
  onSuccess?: (imgValue: string) => void;
  onError?: () => void;
  asyncFunc: (_: File) => Promise<unknown>;
}) => {
  const inputRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const [image, setImage] = useState<any>(null); //this should be in base64 format. only use this to display image
  const [loading, setLoading] = useState(false);

  const toast = useToast();

  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<any>(1);

  const onCropComplete = (
    _croppedAreaPercentage: Area,
    croppedAreaPixels: Area
  ) => {
    setCroppedArea(croppedAreaPixels);
  };

  const handleSubmit = async (imageSrc: any, crop: any) => {
    setLoading(true);

    const canvas: any = await getCroppedImg(imageSrc, crop);
    const dataUrl = canvas.toDataURL();

    await uploadImgToServer(dataUrl);
    setLoading(false);
  };

  const uploadImgToServer = async (dataUrl: string) => {
    setLoading(true);

    const blobImg = base64ToBlob(dataUrl);
    const minimizedImage: any = await resizeFile({ file: blobImg });
    const convertedToFile = dataURLtoFile(minimizedImage, 'useravt.png');

    try {
      await asyncFunc(convertedToFile).then((res: any) => {
        onSuccess(res.data);
      });
      toast('Đổi ảnh đại diện thành công', 'success');
      toggle();
    } catch (err) {
      toast('Có lỗi xảy ra.', 'error');
      onError();
    }

    setLoading(false);
  };

  const onSelectFile = async (event: any) => {
    try {
      setLoading(true);
      if (event.target.files && event.target.files.length > 0) {
        const reader: any = new FileReader();
        await reader.readAsDataURL(event.target.files[0]);

        await reader.addEventListener('load', async () => {
          if (reader?.result !== null) {
            let imgFileSrc = reader?.result.toString();
            imgFileSrc = await detectHEIC(imgFileSrc);
            imgFileSrc = await resizeFile({
              file: base64ToBlob(imgFileSrc),
              maxWidth: MAX_CANVAS_SIZE,
              maxHeight: MAX_CANVAS_SIZE,
            });
            setImage(imgFileSrc);
          }
        });
      }
    } catch (err) {
      toast('Có lỗi xảy ra.', 'error');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      setImage(null);
      setLoading(false);
      setCroppedArea(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={toggle} fullWidth maxWidth={'xs'}>
      <div className={`relative flex w-full justify-center`}>
        <div
          className={`flex w-full flex-col justify-center overflow-hidden rounded-2xl bg-white p-5`}
        >
          {image && (
            <>
              <div className="relative z-10 w-full space-y-6">
                <div className="cropper h-72">
                  <Cropper
                    image={image} //accespt base64 format
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
              </div>
              <Slider
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e, zoom) => setZoom(zoom)}
              />
            </>
          )}

          <div className="flex w-full justify-center gap-5 p-5">
            <button
              className="btn blue-gradient px-4"
              onClick={() => inputRef?.current?.click()}
            >
              Chọn ảnh
            </button>
            {image && (
              <button
                className="btn blue-gradient px-4"
                onClick={async () => {
                  await handleSubmit(image, croppedArea);
                }}
              >
                Đồng ý
              </button>
            )}
          </div>
        </div>

        <GreyLoadingOverlay show={loading} size={25} />

        <input
          type="file"
          ref={inputRef}
          onChange={onSelectFile}
          accept="image/*"
          style={{ display: 'none' }}
        />
      </div>
    </Dialog>
  );
};

export default AvatarCropper;
